import {
	Component,
	OnInit,
	forwardRef,
	OnDestroy,
	Input,
	ElementRef,
	Optional,
	Self,
	HostBinding,
	ViewChild,
	AfterViewInit,
	Output,
	EventEmitter,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	SimpleChanges,
	OnChanges,
} from "@angular/core";
import { FormControl, ControlValueAccessor, NgControl } from "@angular/forms";
import {
	MatAutocompleteSelectedEvent,
	MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { MatFormFieldControl } from "@angular/material/form-field";
import { Subject, Observable } from "rxjs";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { FocusMonitor } from "@angular/cdk/a11y";
import { startWith, map, filter, debounceTime } from "rxjs/operators";
import { MatInput } from "@angular/material/input";
import { ShoppingCartService } from "app/shopping-cart/shopping-cart.service";

@Component({
    selector: "trycatch-mat-auto-complete",
    templateUrl: "./mat-auto-complete.component.html",
    styleUrls: ["./mat-auto-complete.component.scss"],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: forwardRef(() => MatAutoCompleteComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MatAutoCompleteComponent<T>
	implements
		OnInit,
		ControlValueAccessor,
		MatFormFieldControl<T>,
		AfterViewInit,
		OnChanges,
		OnDestroy {
	// Public Variables
	static nextId = 0;
	public __label: string;
	// Public Internal Variables
	stateChanges = new Subject<void>();
	focused = false;
	controlType = "auto-complete";
	autofilled?: boolean;
	_control: FormControl = new FormControl();
	_options: T[];
	_filteredOptionsAsync: Observable<T[]>;
	_filteredOptions: T[];
	_filterText: string;
	_placeholder: string;
	// Output Variables
	@Output() optionSelected = new EventEmitter<T>();
	@Output() inputChanged = new EventEmitter();
	// Host Binding
	@HostBinding("attr.aria-describedby") __describedBy = "";
	@HostBinding("id")
	id = `auto-complete-${MatAutoCompleteComponent.nextId++}`;
	@HostBinding("class.example-floating") _shouldLabelFloat = this
		.shouldLabelFloat;
	// View Variables
	@ViewChild("autoSelectInput", {
		static: true,
		read: MatAutocompleteTrigger,
	})
	autoSelectInputTrigger: MatAutocompleteTrigger;
	@ViewChild("autoSelectInput") autoSelectInput: ElementRef;
	// Static Variables
	// Input Variables
	@Input() maxLength = 300;
	@Input() dealWithNullKeyOption = false;
	@Input() customPanelWidth = "auto";
	@Input() borderBottom = false;
	@Input() focusedElement = false;
	@Input() displayFn: (option?: T) => string | undefined;
	@Input() optionDisplayFn: (option?: T) => string | undefined;
	@Input() optionClassFn: (option?: T) => string | undefined;
	@Input() options$: Observable<T[]>;
	@Input() displayProperty: string;
	@Input() debounceTimeValue = 150;
	@Input() charThreshold = 2;
	@Input() classList: string;
	// Private Variables
	private __data: T;
	private __required = false;
	private __disabled = false;
	// Private Method Definitions
	private __inputValueChanged$: Observable<any>;
	private __propagateChange = (_: any) => {};
	private __propagateTouch = () => {};
	@Input() get placeholder(): string {
		return this._placeholder;
	}
	@Input() get required(): boolean {
		return this.__required;
	}
	@Input() get disabled(): boolean {
		return this.__disabled;
	}
	@Input() get value(): T | undefined {
		return this.__data || undefined;
	}
	@Input() set label(value: string) {
		this.__label = value;
	}
	@Input() set options(val: T[]) {
		if (val) {
			this._options = val;
			this._filteredOptions = this._options;
			this.__subscribeToInputChangeForLocalFilter();
			this.cd.markForCheck();
		}
	}
	// Getters
	get errorState() {
		return this.ngControl.errors !== null && !!this.ngControl.touched;
	}
	get empty() {
		return !this.__data;
	}
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}
	// Setters
	set placeholder(val: string) {
		this._placeholder = val;
		this.stateChanges.next();
	}
	set required(value: boolean) {
		this.__required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	set disabled(value: boolean) {
		this.__disabled = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	set value(val: T | null) {
		this.__data = val;
		this.stateChanges.next();
	}

	// * ----------------------------------------------------------------
	// * @ Constructor
	// * ----------------------------------------------------------------

	constructor(
		private fm: FocusMonitor,
		private cd: ChangeDetectorRef,
		private elRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl
	) {
		fm.monitor(elRef.nativeElement, true).subscribe((origin) => {
			if (this.focused && !origin) {
				this.__propagateTouch();
			}
			this.focused = !!origin;
			this.stateChanges.next();
		});

		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		this.__inputValueChanged$ = this._control.valueChanges.pipe(
			startWith(""),
			filter((val) => typeof val === "string"),
			map((val) => (val && val !== "" ? val.toLowerCase() : undefined)),
			map((val) => {
				this._filterText = val;
				return val;
			})
		);
	}

	// * ----------------------------------------------------------------
	// * @ Interface functions
	// * ----------------------------------------------------------------

	ngOnInit() {
		this.__inputValueChanged$
			.pipe(
				debounceTime(this.debounceTimeValue),
				filter(
					(val) =>
						(val && val.trim().length >= this.charThreshold) || !val
				)
			)
			.subscribe((val) => this.inputChanged.emit(val));

		if (!this.displayFn) {
			this.displayFn = this.__displayFn;
		}

		this.optionDisplayFn = this.optionDisplayFn || this.__displayFn;
		this.optionClassFn = this.optionClassFn || this.__classFn;
	}

	ngAfterViewInit() {
		this.autoSelectInputTrigger.panelClosingActions
			.pipe(filter((e) => !e))
			.subscribe(() => {
				const activeOption = this.autoSelectInputTrigger.activeOption;

				if (activeOption) {
					const val = activeOption.value;
					this.__controlValue = val;
				} else {
					const matchingOption = this.__matchingOption;
					if (matchingOption) {
						this.__controlValue = matchingOption;
					} else {
						if (!this.__data || this._filterText === undefined) {
							this.__controlValue = null;
						} else {
							this.__controlValue = this.__data;
						}
					}
				}
			});

		if (this.focusedElement) {
			this.autoSelectInput.nativeElement.focus();
		}
	}

	ngOnChanges(changes: any) {
		if (changes["focusedElement"]) {
			console.log("THE FOCUSED ATTRIBUTE WAS CHANGED");
			if (this.focusedElement) {
				this.autoSelectInput?.nativeElement?.focus();
			}
		}
	}

	ngOnDestroy(): void {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef.nativeElement);
	}

	writeValue(obj: any): void {
		this.__data = obj;
		this._control.setValue(obj);
	}
	registerOnChange(fn: any): void {
		this.__propagateChange = fn;
	}
	registerOnTouched(fn: any): void {
		this.__propagateTouch = fn;
	}
	setDisabledState?(isDisabled: boolean): void {
		this.__disabled = isDisabled;
		if (this.__disabled) {
			this._control.disable();
		} else {
			this._control.enable();
		}
	}

	setDescribedByIds(ids: string[]): void {
		this.__describedBy = ids.join(" ");
	}
	onContainerClick(event: MouseEvent): void {
		if ((event.target as Element).tagName.toLowerCase() !== "input") {
			this.elRef.nativeElement.querySelector("input")!.focus();
		}
	}

	// * ----------------------------------------------------------------
	// * @ function bound with template
	// * ----------------------------------------------------------------

	_optionSelected(event: MatAutocompleteSelectedEvent) {
		const val = event.option.value;
		this.__data = val;
		this.__propagateChange(val);
		this.optionSelected.emit(val);
	}

	_displayFn() {
		return (val?: any) => this.displayFn(val);
	}

	_optionDisplayFn(option?: any): string | undefined {
		return this.optionDisplayFn(option);
	}

	_optionClassFn(option?: any): string | undefined {
		return this.optionClassFn(option);
	}

	// * ----------------------------------------------------------------
	// * @ private functions
	// * ----------------------------------------------------------------

	private __subscribeToInputChangeForLocalFilter() {
		this.__inputValueChanged$
			.pipe(map((val) => (val ? this.__filter() : this._options)))
			.subscribe((options) => (this._filteredOptions = options));
	}

	private __isPrimitiveType(val: any): boolean {
		const primitiveTypes = [
			"boolean",
			"number",
			"string",
			"undefined",
			"null",
		];
		const valType = typeof val;
		return primitiveTypes.indexOf(valType) >= 0;
	}

	private set __controlValue(val: T) {
		if (
			this.dealWithNullKeyOption &&
			val["Key"] &&
			val["Key"].toLowerCase() === "null"
		) {
			val = this.__data;
		}
		this._control.setValue(val);
		if (val === null) {
			this._control.setValue("");
		}
		this._filterText = null;
		this.__data = val;
		this.__propagateChange(val);
		this.optionSelected.emit(val);
	}

	private get __matchingOption(): T {
		const matchingOptions = this.__matchingOptions;
		if (matchingOptions.length >= 1) {
			const fullMatch =
				matchingOptions.filter(
					(mo) =>
						this.displayFn(mo).toLowerCase() === this._filterText
				)[0] || undefined;

			if (fullMatch) {
				return fullMatch;
			}

			return matchingOptions[0];
		}
		return undefined;
	}

	private get __matchingOptions(): T[] {
		return this._options
			? this._options.filter(
					(option) =>
						this.displayFn(option)
							.toLowerCase()
							.indexOf(this._filterText) > -1
			)
			: [];
	}

	private __filter(): T[] {
		return this._options.filter(
			(option) =>
				this.displayFn(option).toLowerCase().indexOf(this._filterText) >
				-1
		);
	}

	private __displayFn(option?: any): string | undefined {
		if (!option) {
			return undefined;
		}
		return this.__isPrimitiveType(option)
			? option
			: option[this.displayProperty];
	}

	private __classFn(option?: any): string | undefined {
		return undefined;
	}
}
